export const M4_UNIT_8 = {
  id: "m4-u8-transportation-travel",
  title: "Unidad 8: Getting Around",
  topic: "Master transportation vocabulary and travel expressions! Learn how to talk about your commute, trips, and getting around the city. Essential for daily life, planning vacations, and navigating Canada!",
  iconType: "bus",
  grammarImage: "/grammar/grammar-m4-u8.jpg",
  grammarText: "Transportation vocabulary + verb tenses: I TAKE the bus (routine - simple), I'M TAKING the train (now - continuous), I FEEL tired after my commute (feelings + transport). Prepositions: BY bus, ON the bus, IN a car, ON foot.",
  
  sets: [
    // SET 1: Basic Transport Vocabulary (UK vs US)
    {
      id: "m4-u8-set1",
      title: "Set 1: Ways to Travel",
      questions: [
        {
          type: "match",
          q: "Match UK vs Canadian/US term:",
          pairs: [
            { left: "Underground (UK)", right: "Subway (Canada/US)" },
            { left: "Lorry (UK)", right: "Truck (Canada/US)" },
            { left: "Petrol (UK)", right: "Gas (Canada/US)" }
          ],
          a: "completed",
          successtip: "✓ UK vs NORTH AMERICA! Important! 🌍 In Canada: subway, truck, gas. In UK: underground/tube, lorry, petrol. Know both for travel!"
        },
        {
          type: "mc",
          q: "'I ___ the subway to work every day.' (routine)",
          options: ["take", "am taking", "took"],
          a: "take",
          tip: "Routine = present simple! 'I take the bus' = mi rutina diaria."
        },
        {
          type: "input",
          q: "Complete: 'She usually ___ by car.' (travel)",
          a: "travels"
        },
        {
          type: "order",
          q: "Ordena: 'Voy al trabajo en autobús'",
          words: ["I", "go", "to", "work", "by", "bus."],
          a: "I go to work by bus.",
          tip: "Preposition! BY + transportation (no article). 'By bus/train/car' ✓"
        },
        {
          type: "mc",
          q: "'He ___ his bike to school right now.'",
          options: ["is riding", "rides", "ride"],
          a: "is riding",
          tip: "'Right now' = continuous! Riding = conducir bicicleta."
        },
        {
          type: "input",
          q: "Complete: 'We ___ on foot when the weather is nice.' (walk)",
          a: "walk"
        },
        {
          type: "mc",
          q: "In Toronto: 'I take the ___ to downtown.' (metro)",
          options: ["subway", "underground", "metro"],
          a: "subway",
          successtip: "✓ TORONTO! TTC! 🚇 Toronto Transit Commission = TTC. 'I take the TTC' = common phrase. Subway/streetcar/bus!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella conduce al trabajo todos los días'",
          words: ["She", "drives", "to", "work", "every", "day."],
          a: "She drives to work every day."
        },
        {
          type: "input",
          q: "Complete: 'They ___ the bus together.' (catch/take)",
          a: "catch / take"
        },
        {
          type: "mc",
          q: "'I need to fill up with ___ before the road trip.' (Canada)",
          options: ["gas", "petrol", "fuel"],
          a: "gas",
          tip: "'Gas' in North America = 'petrol' in UK. 'Fill up' = llenar el tanque!"
        }
      ]
    },

    // SET 2: Public Transit
    {
      id: "m4-u8-set2",
      title: "Set 2: Public Transportation",
      questions: [
        {
          type: "match",
          q: "Match transit vocabulary:",
          pairs: [
            { left: "fare", right: "tarifa/precio" },
            { left: "transfer", right: "transbordo" },
            { left: "pass", right: "pase/abono" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'I ___ a monthly pass for the TTC.'",
          options: ["have", "am having", "has"],
          a: "have",
          tip: "'Have a pass' = possession = simple. Not continuous!"
        },
        {
          type: "input",
          q: "Complete: 'The bus ___ at 7:30am.' (arrive - schedule)",
          a: "arrives"
        },
        {
          type: "order",
          q: "Ordena: 'Necesito hacer transbordo en la estación Bloor'",
          words: ["I", "need", "to", "transfer", "at", "Bloor", "station."],
          a: "I need to transfer at Bloor station.",
          successtip: "✓ TRANSFER! Key word! 🔄 'Transfer' = cambiar de línea/autobús. 'Where do I transfer?' = pregunta esencial!"
        },
        {
          type: "mc",
          q: "'How much is the ___?' (price for one trip)",
          options: ["fare", "pass", "transfer"],
          a: "fare"
        },
        {
          type: "input",
          q: "Complete: 'She ___ for the bus at the stop.' (wait - now)",
          a: "is waiting"
        },
        {
          type: "mc",
          q: "'The train ___ delayed because of the snow.'",
          options: ["is", "be", "being"],
          a: "is",
          successtip: "✓ DELAYS! Canadian winter! ❄️ Snow delays = common! 'The train is delayed' = announcement you'll hear often!"
        },
        {
          type: "order",
          q: "Ordena: 'El autobús está lleno, esperaré el próximo'",
          words: ["The", "bus", "is", "full,", "I'll", "wait", "for", "the", "next", "one."],
          a: "The bus is full, I'll wait for the next one."
        },
        {
          type: "input",
          q: "Complete: 'I usually ___ off at Main Street.' (get off)",
          a: "get"
        },
        {
          type: "mc",
          q: "Phrasal verb: 'I ___ on the bus at King Station.'",
          options: ["get", "go", "put"],
          a: "get",
          tip: "'Get ON' = subir (bus/train). 'Get OFF' = bajar. 'Get IN/OUT' = car!"
        }
      ]
    },

    // SET 3: Commuting & Feelings
    {
      id: "m4-u8-set3",
      title: "Set 3: Your Daily Commute",
      questions: [
        {
          type: "match",
          q: "Match commute situation to feeling:",
          pairs: [
            { left: "Traffic jam", right: "frustrated" },
            { left: "Missed the bus", right: "annoyed" },
            { left: "Found a seat", right: "relieved" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'My commute ___ an hour each way.' (takes - routine)",
          options: ["takes", "is taking", "took"],
          a: "takes"
        },
        {
          type: "input",
          q: "Complete: 'I feel ___ because the subway is so crowded.' (stressed)",
          a: "stressed"
        },
        {
          type: "order",
          q: "Ordena: 'Me siento cansado después de mi viaje al trabajo'",
          words: ["I", "feel", "tired", "after", "my", "commute."],
          a: "I feel tired after my commute.",
          tip: "'Commute' = viaje diario al trabajo. 'My commute is long' = common complaint!"
        },
        {
          type: "mc",
          q: "'The bus ___ really late today, so I'm frustrated.'",
          options: ["is running", "runs", "run"],
          a: "is running",
          tip: "'Running late' = llegar tarde (schedule). 'The train is running 10 minutes late' ✓"
        },
        {
          type: "input",
          q: "Complete: 'She ___ relaxed when she gets a seat on the train.' (feel)",
          a: "feels"
        },
        {
          type: "mc",
          q: "'I'm ___ fed up with traffic!'",
          options: ["so", "a bit", "not"],
          a: "so",
          successtip: "✓ TRAFFIC! Universal frustration! 🚗😤 'Fed up with traffic' = every commuter's complaint. Toronto/Vancouver = notorious traffic!"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy esperando el autobús en la nieve y estoy congelado'",
          words: ["I'm", "waiting", "for", "the", "bus", "in", "the", "snow", "and", "I'm", "freezing."],
          a: "I'm waiting for the bus in the snow and I'm freezing."
        },
        {
          type: "input",
          q: "Complete: 'He feels ___ because he missed his connection.' (annoyed/frustrated)",
          a: "annoyed / frustrated"
        },
        {
          type: "mc",
          q: "'I usually ___ the 8am bus, but today I ___ the 9am one because I'm late.'",
          options: ["take, am taking", "am taking, take", "take, take"],
          a: "take, am taking",
          tip: "Mix tenses! 'Usually take' (routine) vs 'today am taking' (exception). Natural English!"
        }
      ]
    },

    // SET 4: Cars & Driving
    {
      id: "m4-u8-set4",
      title: "Set 4: Driving & Roads",
      questions: [
        {
          type: "match",
          q: "Match driving vocabulary:",
          pairs: [
            { left: "license", right: "licencia de conducir" },
            { left: "highway", right: "autopista/carretera" },
            { left: "parking", right: "estacionamiento" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'I ___ my driver's license last year.'",
          options: ["got", "get", "am getting"],
          a: "got",
          tip: "'Got' = past! Last year = pasado. Simple past for completed action."
        },
        {
          type: "input",
          q: "Complete: 'She ___ to work every day.' (drive - routine)",
          a: "drives"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy buscando estacionamiento'",
          words: ["I'm", "looking", "for", "parking."],
          a: "I'm looking for parking.",
          successtip: "✓ PARKING! Nightmare! 🅿️ Downtown parking = expensive and scarce! 'Looking for parking' = daily struggle!"
        },
        {
          type: "mc",
          q: "'The ___ is really bad during rush hour.' (congestion)",
          options: ["traffic", "car", "bus"],
          a: "traffic"
        },
        {
          type: "input",
          q: "Complete: 'He's ___ on the highway right now.' (drive - continuous)",
          a: "driving"
        },
        {
          type: "mc",
          q: "Canadian term: 'Take the 401 ___.' (direction word)",
          options: ["eastbound", "to east", "east way"],
          a: "eastbound",
          tip: "'Eastbound/westbound' = dirección en autopistas. 'The 401 eastbound' = highway direction!"
        },
        {
          type: "order",
          q: "Ordena: 'Necesito llenar el tanque de gasolina'",
          words: ["I", "need", "to", "fill", "up", "the", "gas", "tank."],
          a: "I need to fill up the gas tank."
        },
        {
          type: "input",
          q: "Complete: 'Where can I ___ my car?' (park)",
          a: "park"
        },
        {
          type: "mc",
          q: "'I'm ___ my G2 test next week.' (taking driving test)",
          options: ["taking", "take", "took"],
          a: "taking",
          successtip: "✓ G2 TEST! Ontario driving! 🚗 G1 → G2 → G (full license). 'Taking my G2' = common phrase for immigrants!"
        }
      ]
    },

    // SET 5: Mini-Reading - Morning Commute
    {
      id: "m4-u8-set5",
      title: "Set 5: Commute Story",
      questions: [
        {
          type: "mc",
          q: "Lee:\n\n'Every morning, I take the subway to work. I usually catch the 7:30am train at Finch Station. The commute takes about 40 minutes. Right now, I'm sitting on the train and I'm feeling pretty tired because I didn't sleep well. The subway is really crowded today, so I'm standing near the door. I always transfer at Bloor-Yonge station to get the Line 2 eastbound.'\n\n¿Qué hace el narrador cada mañana?",
          options: ["Takes the subway to work", "Drives to work", "Walks to work"],
          a: "Takes the subway to work"
        },
        {
          type: "mc",
          q: "¿Cuánto tiempo toma su commute?",
          options: ["40 minutes", "30 minutes", "1 hour"],
          a: "40 minutes"
        },
        {
          type: "input",
          q: "¿Cómo se siente hoy? → He's feeling pretty ___.",
          a: "tired"
        },
        {
          type: "mc",
          q: "¿Por qué está de pie (standing)?",
          options: ["The subway is crowded", "He prefers to stand", "There are no trains"],
          a: "The subway is crowded"
        },
        {
          type: "mc",
          q: "El texto usa 'usually' con:",
          options: ["Present simple (routine)", "Present continuous", "Past simple"],
          a: "Present simple (routine)",
          tip: "'Usually catch' = routine = simple. 'Right now sitting' = now = continuous!"
        },
        {
          type: "mc",
          q: "¿Dónde hace transbordo?",
          options: ["Bloor-Yonge station", "Finch Station", "Union Station"],
          a: "Bloor-Yonge station",
          successtip: "✓ BLOOR-YONGE! Major transfer! 🚇 Busiest intersection in Toronto subway. Line 1 + Line 2. Always crowded!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ transfer' (always - frequency)",
          a: "always"
        },
        {
          type: "order",
          q: "Traduce del contexto: 'Tomo la línea 2 hacia el este'",
          words: ["I", "take", "Line", "2", "eastbound."],
          a: "I take Line 2 eastbound."
        },
        {
          type: "mc",
          q: "El narrador mezcla dos tiempos verbales para mostrar:",
          options: ["Routine (simple) and right now (continuous)", "Only present simple", "Only continuous"],
          a: "Routine (simple) and right now (continuous)"
        },
        {
          type: "mc",
          q: "Lee continuación:\n\n'Today the train is running late because of signal problems. I'm feeling SO frustrated because I'm going to be late for work. I usually arrive at 8:15am, but today I'm arriving at 8:45am. My boss isn't going to be happy!'\n\n¿Por qué está retrasado el tren?",
          options: ["Signal problems", "Snow", "Crowded"],
          a: "Signal problems",
          successtip: "✓ SIGNAL PROBLEMS! TTC classic! 🚨 'Signal problems' = frequent TTC excuse. Also: 'Track issues', 'Mechanical failure'. Know these announcements!"
        }
      ]
    },

    // SET 6: Travel & Vacations
    {
      id: "m4-u8-set6",
      title: "Set 6: Going on Trips",
      questions: [
        {
          type: "match",
          q: "Match travel vocabulary:",
          pairs: [
            { left: "flight", right: "vuelo" },
            { left: "luggage", right: "equipaje" },
            { left: "boarding pass", right: "pase de abordar" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'We ___ to Mexico next month!' (future plan)",
          options: ["are flying", "fly", "flew"],
          a: "are flying",
          tip: "'Next month' with continuous = future plan! 'We're flying' = ya planeado."
        },
        {
          type: "input",
          q: "Complete: 'She ___ her luggage at the airport.' (check in - now)",
          a: "is checking in"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy tan emocionado por mis vacaciones'",
          words: ["I'm", "so", "excited", "about", "my", "vacation!"],
          a: "I'm so excited about my vacation!"
        },
        {
          type: "mc",
          q: "'My ___ leaves at 6am tomorrow.' (airplane trip)",
          options: ["flight", "fly", "plane"],
          a: "flight"
        },
        {
          type: "input",
          q: "Complete: 'They're ___ a road trip to Vancouver.' (take - continuous future)",
          a: "taking"
        },
        {
          type: "mc",
          q: "Phrasal verb: 'We need to ___ away for a weekend!' (escape/vacation)",
          options: ["get", "go", "take"],
          a: "get",
          successtip: "✓ GET AWAY! Phrasal verb! 🏖️ 'Get away' = escaparse/tomar vacaciones. 'I need to get away from work' = necesito descansar!"
        },
        {
          type: "order",
          q: "Ordena: 'Vamos a pasar el fin de semana en Niagara Falls'",
          words: ["We're", "going", "to", "spend", "the", "weekend", "in", "Niagara", "Falls."],
          a: "We're going to spend the weekend in Niagara Falls.",
          successtip: "✓ NIAGARA! Classic Canadian trip! 🌊 2-hour drive from Toronto. Weekend getaway favorite for Ontario residents!"
        },
        {
          type: "input",
          q: "Complete: 'I feel ___ when I travel.' (relaxed/happy)",
          a: "relaxed / happy"
        },
        {
          type: "mc",
          q: "'Where are you ___ this summer?' (going for vacation)",
          options: ["going", "go", "went"],
          a: "going",
          tip: "'Where are you going?' = common question for future plans. Can also say 'Where are you traveling?'"
        }
      ]
    },

    // SET 7: Airports & Flying
    {
      id: "m4-u8-set7",
      title: "Set 7: Air Travel",
      questions: [
        {
          type: "match",
          q: "Match airport process:",
          pairs: [
            { left: "Check in", right: "Registrarse/facturar" },
            { left: "Security", right: "Seguridad" },
            { left: "Boarding", right: "Abordaje" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'Please proceed to ___ for your flight to Calgary.'",
          options: ["gate 24", "door 24", "station 24"],
          a: "gate 24",
          tip: "'Gate' = puerta de embarque. 'Boarding at gate 24' = airport announcement!"
        },
        {
          type: "input",
          q: "Complete: 'I need to ___ in two hours before my international flight.' (check in)",
          a: "check in"
        },
        {
          type: "order",
          q: "Ordena: 'Mi vuelo está retrasado por tres horas'",
          words: ["My", "flight", "is", "delayed", "by", "three", "hours."],
          a: "My flight is delayed by three hours."
        },
        {
          type: "mc",
          q: "'I'm feeling ___ because my flight is cancelled!' (frustrated/annoyed)",
          options: ["SO annoyed", "a bit happy", "not worried"],
          a: "SO annoyed"
        },
        {
          type: "input",
          q: "Complete: 'We're ___ through security now.' (go - continuous)",
          a: "going"
        },
        {
          type: "mc",
          q: "'Your ___ is now boarding at gate 12.'",
          options: ["flight", "plane", "trip"],
          a: "flight",
          successtip: "✓ BOARDING! Process! ✈️ Check in → Security → Wait at gate → Boarding → Flight. Know the sequence!"
        },
        {
          type: "order",
          q: "Ordena: 'Necesito recoger mi equipaje en el carrusel 5'",
          words: ["I", "need", "to", "pick", "up", "my", "luggage", "at", "carousel", "5."],
          a: "I need to pick up my luggage at carousel 5."
        },
        {
          type: "input",
          q: "Complete: 'She always feels ___ during takeoff.' (nervous)",
          a: "nervous"
        },
        {
          type: "mc",
          q: "'I'm flying ___ Canada to visit family back home.'",
          options: ["out of", "in", "from of"],
          a: "out of",
          tip: "'Out of Canada' = saliendo de Canadá. 'Flying out of Toronto' = departing from!"
        }
      ]
    },

    // SET 8: Weekend Activities & Social Plans
    {
      id: "m4-u8-set8",
      title: "Set 8: Social Transportation",
      questions: [
        {
          type: "match",
          q: "Match social activity expression:",
          pairs: [
            { left: "go out (with friends)", right: "salir (con amigos)" },
            { left: "meet up", right: "encontrarse/reunirse" },
            { left: "pick someone up", right: "recoger a alguien" }
          ],
          a: "completed",
          successtip: "✓ SOCIAL PHRASAL VERBS! Natural English! 🎉 'Go out', 'meet up', 'pick up' = HOW natives talk about plans!"
        },
        {
          type: "mc",
          q: "'We're ___ at the mall at 2pm.' (meeting)",
          options: ["meeting up", "going out", "hanging"],
          a: "meeting up",
          tip: "'Meet up' = quedar/encontrarse. 'Let's meet up at Starbucks' = common phrase!"
        },
        {
          type: "input",
          q: "Complete: 'Can you ___ me up at 7pm?' (pick up - give a ride)",
          a: "pick"
        },
        {
          type: "order",
          q: "Ordena: 'Voy a salir con mis amigos este fin de semana'",
          words: ["I'm", "going", "out", "with", "my", "friends", "this", "weekend."],
          a: "I'm going out with my friends this weekend.",
          successtip: "✓ GOING OUT! Social life! 🎊 'Go out' = salir a divertirse. 'Wanna go out Friday?' = invitation common phrase!"
        },
        {
          type: "mc",
          q: "Alternative to 'go out': 'We're ___ this Saturday.' (spending time casually)",
          options: ["hanging out", "going on", "taking out"],
          a: "hanging out",
          tip: "'Hang out' = pasar el rato. More casual than 'go out'. 'Just hanging out' = relajado!"
        },
        {
          type: "input",
          q: "Complete: 'She's ___ me to the airport.' (drive - giving a ride)",
          a: "driving"
        },
        {
          type: "mc",
          q: "'I'm ___ a ride with Carlos to the party.'",
          options: ["getting", "taking", "having"],
          a: "getting",
          tip: "'Get a ride' = que alguien te lleve. 'Can I get a ride?' = common favor request!"
        },
        {
          type: "order",
          q: "Ordena: 'Nos vamos a encontrar en el centro'",
          words: ["We're", "meeting", "up", "downtown."],
          a: "We're meeting up downtown."
        },
        {
          type: "input",
          q: "Complete: 'Do you want to ___ out this weekend?' (hang - social)",
          a: "hang"
        },
        {
          type: "mc",
          q: "'I'm giving my friend a ___ to work.' (ride/lift)",
          options: ["ride", "car", "drive"],
          a: "ride",
          successtip: "✓ GIVE A RIDE! Helping out! 🚗 'Give someone a ride/lift' = llevar a alguien. Canadian kindness in action!"
        }
      ]
    },

    // SET 9: Transportation Problems & Solutions
    {
      id: "m4-u8-set9",
      title: "Set 9: When Things Go Wrong",
      questions: [
        {
          type: "match",
          q: "Match problem to solution:",
          pairs: [
            { left: "Missed the bus", right: "Wait for the next one" },
            { left: "Car broke down", right: "Call a tow truck" },
            { left: "Flight cancelled", right: "Rebook or get refund" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "'My car ___ on the highway! I need help!' (stopped working)",
          options: ["broke down", "broke up", "broke out"],
          a: "broke down",
          tip: "'Break down' = descomponerse (vehicle). 'My car broke down' = emergency phrase!"
        },
        {
          type: "input",
          q: "Complete: 'The train is ___ because of bad weather.' (cancelled)",
          a: "cancelled"
        },
        {
          type: "order",
          q: "Ordena: 'Perdí mi vuelo y ahora estoy muy estresado'",
          words: ["I", "missed", "my", "flight", "and", "now", "I'm", "really", "stressed."],
          a: "I missed my flight and now I'm really stressed."
        },
        {
          type: "mc",
          q: "'I'm ___ frustrated because I've been waiting for an hour!'",
          options: ["SO", "a bit", "not very"],
          a: "SO"
        },
        {
          type: "input",
          q: "Complete: 'We need to ___ a taxi because the bus isn't coming.' (call)",
          a: "call"
        },
        {
          type: "mc",
          q: "'The subway is ___ service today.' (not operating)",
          options: ["out of", "in", "on"],
          a: "out of",
          successtip: "✓ OUT OF SERVICE! Common announcement! 🚫 'Line 1 is out of service' = replacement buses. TTC reality!"
        },
        {
          type: "order",
          q: "Ordena: 'Mi carro se descompuso, entonces llamé un Uber'",
          words: ["My", "car", "broke", "down,", "so", "I", "called", "an", "Uber."],
          a: "My car broke down, so I called an Uber."
        },
        {
          type: "input",
          q: "Complete: 'I feel ___ because I'm stuck in traffic.' (fed up/frustrated)",
          a: "fed up / frustrated"
        },
        {
          type: "mc",
          q: "'The bus driver just ___ me!' (drove past without stopping)",
          options: ["passed me by", "picked me up", "dropped me off"],
          a: "passed me by",
          tip: "'Pass by' = pasar de largo (without stopping). Frustrating bus experience!"
        }
      ]
    },

    // SET 10: Integration - Real Trip Planning
    {
      id: "m4-u8-set10",
      title: "Set 10: Planning a Trip",
      questions: [
        {
          type: "mc",
          q: "'We ___ to Montreal next weekend by train.' (future plan)",
          options: ["are going", "go", "went"],
          a: "are going"
        },
        {
          type: "input",
          q: "Complete: 'How long ___ the trip take?' (does - routine question)",
          a: "does"
        },
        {
          type: "order",
          q: "Ordena: 'Voy a visitar a mi familia y estoy muy emocionado'",
          words: ["I'm", "going", "to", "visit", "my", "family", "and", "I'm", "so", "excited!"],
          a: "I'm going to visit my family and I'm so excited!"
        },
        {
          type: "mc",
          q: "'The train ___ 5 hours from Toronto to Montreal.' (duration - general)",
          options: ["takes", "is taking", "took"],
          a: "takes"
        },
        {
          type: "input",
          q: "Complete: 'I feel ___ about traveling alone for the first time.' (nervous)",
          a: "nervous"
        },
        {
          type: "mc",
          q: "'We're ___ away for the long weekend!' (vacation phrasal)",
          options: ["getting", "going", "taking"],
          a: "getting",
          successtip: "✓ LONG WEEKEND! Canadian life! 🎉 Victoria Day, Canada Day, Civic Holiday = 3-day weekends. Perfect for getaways!"
        },
        {
          type: "order",
          q: "Ordena: 'Usualmente conduzco pero esta vez voy a volar'",
          words: ["I", "usually", "drive,", "but", "this", "time", "I'm", "flying."],
          a: "I usually drive, but this time I'm flying."
        },
        {
          type: "input",
          q: "Complete: 'She ___ the train because it's more relaxing than flying.' (prefer)",
          a: "prefers"
        },
        {
          type: "mc",
          q: "'I need to ___ accommodation before we go.'",
          options: ["book", "take", "get"],
          a: "book",
          tip: "'Book' = reservar (hotel, flight, train). 'I booked a hotel' = past. 'Booking.com' = website!"
        },
        {
          type: "mc",
          q: "Final integration: 'Every day I ___ the subway to work and it ___ 40 minutes. Right now I ___ on the train and I ___ tired because the commute ___ so long. But next month I ___ to Vancouver for vacation and I ___ SO excited about that!'",
          options: [
            "take, takes, am sitting, feel, is, am flying, am",
            "am taking, takes, sit, am feeling, is, fly, feel",
            "take, is taking, am sitting, am feeling, takes, fly, am"
          ],
          a: "take, takes, am sitting, feel, is, am flying, am",
          successtip: "✓ UNIT 8 COMPLETE! Transportation mastery! 🚇✈️🚗 You can now talk about commuting, traveling, and getting around using correct verb tenses + feelings + phrasal verbs! From 'I take the TTC' to 'My car broke down' to 'I'm getting away for the weekend' = real-life English! 💪🇨🇦"
        }
      ]
    }
  ],

  // ===================================================================
// READING SET: Daniela's Commute & Travel Plans
// ===================================================================
readingSet: {
  id: "m4-u8-reading",
  title: "Reading: Transportation in my Life",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Daily Routine (pregunta 1)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Commute**

Hi! My name is Daniela and I want to tell you about transportation in my life. I live in Mississauga and I work in downtown Toronto, so my commute is pretty long. 

Every morning, I drive to the GO station and then I take the train to Union Station. The whole trip takes about 90 minutes each way. I usually feel tired after my commute, especially in winter when it's so cold waiting for the train.`
    },
    {
      type: "mc",
      q: "¿Cómo describe Daniela su trayecto (commute) diario al trabajo?",
      options: [
        "Maneja a la estación GO y luego toma el tren - 90 minutos en total",
        "Solo maneja hasta el centro (downtown)",
        "Solo toma el autobús local"
      ],
      a: "Maneja a la estación GO y luego toma el tren - 90 minutos en total",
      successtip: "✓ READING DETAIL!\nUn trayecto de 90 minutos es muy común para quienes viven en Mississauga y trabajan en Toronto. 🚆"
    },

    

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Right Now (pregunta 3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**On the Train**

Right now, I'm sitting on the GO train and I'm writing this on my phone. The train is pretty crowded today, but I got a seat, so I'm feeling lucky! I'm listening to a podcast while I'm traveling - it makes the time go faster.`
    },
    {
      type: "mc",
      q: "¿Qué está haciendo Daniela en este preciso momento?",
      options: [
        "Sentada en el tren GO, escuchando un podcast",
        "Manejando su carro",
        "Caminando hacia Union Station"
      ],
      a: "Sentada en el tren GO, escuchando un podcast",
      successtip: "✓ PRESENT CONTINUOUS!\nAcciones ocurriendo 'right now'. ¡Aprovechando bien el tiempo! 🎧📱"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Past vs. Present (pregunta 2)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Why the Train?**

I used to drive all the way to downtown, but the traffic was absolutely terrible and parking cost $30 a day! I was SO stressed every morning. Now I prefer taking the train. It's more relaxing and I can read or work on my laptop.`
    },
    {
      type: "mc",
      q: "¿Por qué Daniela dejó de conducir su carro hasta el centro?",
      options: [
        "El tráfico era terrible y el estacionamiento caro ($30 al día)",
        "Su carro se descompuso",
        "Prefiere caminar"
      ],
      a: "El tráfico era terrible y el estacionamiento caro ($30 al día)",
      successtip: "✓ REALITY CHECK!\n$30 dólares al día es un costo real en Toronto. ¡El tren es un gran ahorro! 💰🚗"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Future Plans (preguntas 4-5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Vacation Time!**

Next month, I'm taking a vacation! I'm flying to Colombia to visit my family - I'm SO over the moon about it! I haven't seen them in two years. 

My flight leaves really early (6am), so my friend is giving me a ride to Pearson Airport at 3:30am. I'm already feeling excited but also a bit nervous.`
    },
    {
      type: "mc",
      q: "¿A dónde viajará Daniela el próximo mes?",
      options: ["A Colombia a visitar a su familia", "A Montreal", "A Vancouver"],
      a: "A Colombia a visitar a su familia",
      successtip: "✓ VACATION PLANS!\nUsamos Present Continuous para planes fijos del futuro cercano. ✈️🇨🇴"
    },
    {
      type: "mc",
      q: "¿Por qué Daniela se siente 'over the moon'?",
      options: [
        "Porque no ha visto a su familia en 2 años",
        "Porque consiguió un nuevo trabajo",
        "Porque compró un carro nuevo"
      ],
      a: "Porque no ha visto a su familia en 2 años",
      successtip: "✓ IDIOM!\n'Over the moon' = extremadamente feliz. ¡La reunión familiar es la razón! 🌙✨"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 5: Weekends & Advice (preguntas 6, 8, 9)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Weekends and Tips**

On weekends, I don't use transit much. I usually hang out with friends, and we often meet up at Square One mall or go out to restaurants in Port Credit. 

My advice for newcomers: get a Presto card for public transit! It makes everything easier. And if you're planning a road trip, Canadian highways are amazing - just watch out for winter driving!`
    },
    {
      type: "mc",
      q: "¿Qué 'phrasal verb' usa Daniela para decir 'pasar el rato' con amigos?",
      options: ["Hang out", "Go out", "Meet up"],
      a: "Hang out",
      successtip: "✓ PHRASAL VERB!\n'Hang out' = pasar el tiempo de forma casual. Muy usado en inglés. 👥"
    },
    {
      type: "mc",
      q: "¿Qué es una 'Presto card' según el texto?",
      options: [
        "Una tarjeta para el transporte público en Ontario",
        "Una tarjeta de crédito",
        "Una licencia de conducir"
      ],
      a: "Una tarjeta para el transporte público en Ontario",
      successtip: "✓ PRESTO!\nEsencial para viajar en el TTC, GO Transit y otros sistemas. 💳"
    },
    {
      type: "mc",
      q: "El texto menciona lugares reales. ¿Cuáles son?",
      options: [
        "Union Station, Pearson Airport, Square One, Port Credit",
        "Solo el centro de Toronto",
        "Ningún lugar específico"
      ],
      a: "Union Station, Pearson Airport, Square One, Port Credit",
      successtip: "✓ REAL PLACES!\nConocer estos nombres ayuda a orientarse en el área metropolitana de Toronto. 📍"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 6: Summary & Grammar (preguntas 7, 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Complete Story**

Daniela's story is a great example of how to combine different grammar points to tell a full narrative about life in a new country.`
    },
    {
      type: "mc",
      q: "¿Cuántos tiempos verbales diferentes utiliza Daniela en su relato?",
      options: [
        "4: Presente simple, continuo, 'used to' (hábito pasado) y futuro cercano",
        "Solo presente simple",
        "Solo presente continuo"
      ],
      a: "4: Presente simple, continuo, 'used to' (hábito pasado) y futuro cercano",
      successtip: "✓ GRAMMAR MASTER!\nDominar estos 4 tiempos te permite contar historias completas. 🏆"
    },
    {
      type: "mc",
      q: "En conclusión, el texto combina exitosamente:",
      options: [
        "Vocabulario de transporte, tiempos verbales, sentimientos y lugares reales",
        "Solo vocabulario de transporte",
        "Solo reglas de gramática"
      ],
      a: "Vocabulario de transporte, tiempos verbales, sentimientos y lugares reales",
      successtip: "✓ AUTHENTIC STORY! 🎉\n¡Felicidades! Has comprendido una historia auténtica de la experiencia inmigrante en Canadá. 🏆"
    }
  ]
},

  // REVIEW SET
  reviewSet: {
    id: "m4-u8-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match transportation method to preposition:",
        pairs: [
          { left: "BY bus/train/car", right: "General method (no article)" },
          { left: "ON the bus/train", right: "Inside the vehicle" },
          { left: "IN a car/taxi", right: "Enclosed vehicle" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Routine: 'I ___ the subway every day.'",
        options: ["take", "am taking", "took"],
        a: "take"
      },
      {
        type: "mc",
        q: "Right now: 'She ___ the bus.'",
        options: ["is taking", "takes", "took"],
        a: "is taking"
      },
      {
        type: "input",
        q: "Phrasal verb for car stopped working: My car ___ down.",
        a: "broke"
      },
      {
        type: "mc",
        q: "UK vs Canada: 'I need ___.' (gasolina)",
        options: ["gas", "petrol", "fuel"],
        a: "gas"
      },
      {
        type: "order",
        q: "Ordena: 'Tomo el metro al trabajo todos los días'",
        words: ["I", "take", "the", "subway", "to", "work", "every", "day."],
        a: "I take the subway to work every day."
      },
      {
        type: "mc",
        q: "Phrasal verb for vacation: 'We're ___ away this weekend.'",
        options: ["getting", "going", "taking"],
        a: "getting"
      },
      {
        type: "input",
        q: "Phrasal verb for meeting socially: Let's ___ up. (meet)",
        a: "meet"
      },
      {
        type: "mc",
        q: "'Transfer' means:",
        options: ["Cambiar de línea/autobús", "Pagar tarifa", "Llegar tarde"],
        a: "Cambiar de línea/autobús"
      },
      {
        type: "mc",
        q: "Final integration: 'I usually ___ to work by bus, but today I ___ an Uber because the bus ___ late. I ___ SO frustrated about transit lately!'",
        options: [
          "go, am taking, is running, am",
          "am going, take, runs, was",
          "go, take, is running, feel"
        ],
        a: "go, am taking, is running, am",
        successtip: "✓ UNIT 8 MASTERED! 🎉 You can now navigate Canadian transportation vocabulary, mix present tenses naturally, express commute frustrations, and use phrasal verbs like a native! From 'I take the TTC' to 'My car broke down' to 'Let's hang out' = real-world English unlocked! 🚇✈️🚗🇨🇦"
      }
    ]
  }
};
